import { FaArrowLeft, FaArrowRight, FaChevronDown } from 'react-icons/fa';
import states from 'services/helpers/states.json';
// Bellagio photo sits on the left and the Eiffel Tower photo on the right, matching the design
import skylineLeft from 'assets/images/vegas-skyline-right.jpeg';
import skylineRight from 'assets/images/vegas-skyline-left.jpeg';

const skylineMask = (corner) => {
  const mask = `radial-gradient(ellipse at ${corner}, #000 35%, transparent 72%)`;
  return { WebkitMaskImage: mask, maskImage: mask };
};

// Shared UI for the Vegas Conference registration pages (Sponsors & Franchise)

export const US_STATE_OPTIONS = states.states.map((s) => ({ value: s.name, label: s.name }));

export const StepWrapper = ({ children, className = '' }) => (
  <div className={`animate-fadeSlide ${className}`}>{children}</div>
);

// Soft sky gradient on top, navy & gold waves at the bottom
export const VegasBackground = ({ children, className = '' }) => (
  <div
    className={`relative min-h-screen overflow-hidden bg-gradient-to-b from-[#eef0fa] via-white to-[#f7f8fc] ${className}`}
  >
    <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(ellipse_at_top_left,rgba(201,162,75,0.18),transparent_55%),radial-gradient(ellipse_at_top_right,rgba(120,130,220,0.18),transparent_55%)]" />

    {/* Las Vegas skyline in the top corners, fading into the page */}
    <img
      src={skylineLeft}
      alt=""
      aria-hidden="true"
      className="pointer-events-none select-none absolute left-0 top-0 w-[45%] sm:w-[34%] max-w-[560px] opacity-40 sm:opacity-90"
      style={skylineMask('top left')}
    />
    <img
      src={skylineRight}
      alt=""
      aria-hidden="true"
      className="pointer-events-none select-none absolute right-0 top-0 w-[45%] sm:w-[34%] max-w-[560px] opacity-40 sm:opacity-90"
      style={skylineMask('top right')}
    />

    <svg
      className="pointer-events-none absolute inset-x-0 bottom-0 h-56 w-full"
      viewBox="0 0 1440 320"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="vegasGold" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#f5dfa6" />
          <stop offset="50%" stopColor="#c9a24b" />
          <stop offset="100%" stopColor="#f5dfa6" />
        </linearGradient>
        <linearGradient id="vegasNavy" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#0d1a4f" />
          <stop offset="100%" stopColor="#1F288F" />
        </linearGradient>
      </defs>
      <path
        d="M0,120 C320,260 760,320 1080,240 C1240,200 1360,150 1440,110 L1440,320 L0,320 Z"
        fill="url(#vegasGold)"
        opacity="0.55"
      />
      <path
        d="M0,150 C320,290 760,340 1080,265 C1240,228 1360,180 1440,140 L1440,320 L0,320 Z"
        fill="url(#vegasNavy)"
      />
      <path
        d="M0,135 C320,275 760,330 1080,252 C1240,214 1360,165 1440,125"
        fill="none"
        stroke="url(#vegasGold)"
        strokeWidth="3"
      />
    </svg>

    <div className="relative z-10">{children}</div>
  </div>
);

export const VegasHeader = ({ title, tagline, subTagline }) => (
  <div className="text-center mb-8">
    <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-[#13235f] mb-3">
      {title}
    </h1>
    <div className="flex items-center justify-center gap-3 sm:gap-5">
      <span className="h-[2px] w-10 sm:w-24 bg-gradient-to-r from-transparent to-[#c9a24b]" />
      <p className="m-0 font-semibold text-sm sm:text-lg tracking-[0.35em] text-[#13235f]">
        {tagline}
      </p>
      <span className="h-[2px] w-10 sm:w-24 bg-gradient-to-l from-transparent to-[#c9a24b]" />
    </div>
    {subTagline && (
      <p className="mt-2 mb-0 font-semibold text-xs tracking-[0.3em] text-slate-500">
        {subTagline}
      </p>
    )}
  </div>
);

export const VegasStepper = ({ currentStep, steps }) => (
  <div className="flex items-start max-w-2xl mx-auto mb-10 px-2">
    {steps.map((label, i) => {
      const stepNo = i + 1;
      const isDone = currentStep > stepNo;
      const isActive = currentStep === stepNo;
      const isLast = i === steps.length - 1;
      const fill = isDone ? '100%' : isActive ? '50%' : '0%';

      return (
        <div key={label} className={`flex items-start ${isLast ? '' : 'flex-1'}`}>
          <div className="flex flex-col items-center w-16">
            <div
              className={`w-11 h-11 rounded-full flex items-center justify-center font-bold transition-all duration-300
                ${
                  isActive || isDone
                    ? 'bg-[#13235f] text-white shadow-[0_0_0_4px_rgba(19,35,95,0.12)]'
                    : 'bg-slate-200 text-slate-600'
                }`}
            >
              {stepNo}
            </div>
            <p
              className={`mt-2 mb-0 text-sm whitespace-nowrap ${
                isActive ? 'text-[#13235f] font-semibold' : 'text-slate-500'
              }`}
            >
              {label}
            </p>
          </div>

          {!isLast && (
            <div className="flex-1 h-[3px] mt-[21px] -mx-2 rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full bg-[#13235f] transition-all duration-500"
                style={{ width: fill }}
              />
            </div>
          )}
        </div>
      );
    })}
  </div>
);

// White card with a gold accent along the top edge
export const VegasCard = ({ children, className = '' }) => (
  <div
    className={`relative max-w-4xl w-full mx-auto bg-white rounded-2xl shadow-[0_10px_40px_rgba(19,35,95,0.08)] overflow-hidden ${className}`}
  >
    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#c9a24b] via-[#f5dfa6] to-[#c9a24b]" />
    <div className="p-5 sm:p-10">{children}</div>
  </div>
);

export const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-4 mb-6">
    {Icon && (
      <div className="w-14 h-14 shrink-0 rounded-full bg-slate-100 flex items-center justify-center text-[#13235f]">
        <Icon size={22} />
      </div>
    )}
    <div>
      <h2 className="m-0 text-xl font-bold text-[#13235f]">{title}</h2>
      {subtitle && <p className="m-0 mt-1 text-slate-500">{subtitle}</p>}
    </div>
  </div>
);

export const SectionDivider = () => <div className="h-px bg-slate-200 my-8" />;

const fieldClasses = (hasError) =>
  `w-full h-14 pl-12 pr-4 rounded-xl border bg-white text-slate-700 outline-none transition
   focus:border-[#13235f] focus:ring-2 focus:ring-[#13235f]/10
   ${hasError ? 'border-red-300' : 'border-slate-200'}`;

// Label sits inside the field (like a placeholder) so the required asterisk can be red
const FieldLabel = ({ label, required }) => (
  <span className="pointer-events-none absolute left-12 top-1/2 -translate-y-1/2 text-slate-400">
    {label}
    {required && <span className="text-red-500"> *</span>}
  </span>
);

export const IconInput = ({
  icon: Icon,
  label,
  required,
  value,
  onChange,
  hasError,
  type = 'text'
}) => (
  <div className="relative">
    {Icon && (
      <Icon className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
    )}
    <input
      type={type}
      aria-label={label}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={fieldClasses(hasError)}
    />
    {!value && <FieldLabel label={label} required={required} />}
  </div>
);

export const IconSelect = ({ icon: Icon, label, required, value, onChange, hasError, options }) => (
  <div className="relative">
    {Icon && (
      <Icon className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
    )}
    <select
      aria-label={label}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={`${fieldClasses(hasError)} appearance-none pr-10 cursor-pointer ${
        value ? '' : 'text-transparent'
      }`}
    >
      <option value="" disabled hidden />
      {options.map((o) => (
        <option key={o.value} value={o.value} className="text-slate-700">
          {o.label}
        </option>
      ))}
    </select>
    {!value && <FieldLabel label={label} required={required} />}
    <FaChevronDown
      size={12}
      className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-500"
    />
  </div>
);

export const VegasFooterButtons = ({ showBack, showContinue, disabled, onBack, onContinue }) => (
  <div className="flex flex-col-reverse sm:flex-row items-center justify-center gap-4 mt-10 pb-16">
    {showBack && (
      <button
        type="button"
        onClick={onBack}
        className="w-full sm:w-44 h-14 rounded-xl border border-slate-300 bg-white font-semibold text-[#13235f] flex items-center justify-center gap-2 transition hover:bg-slate-50"
      >
        <FaArrowLeft size={14} /> Back
      </button>
    )}

    {showContinue && (
      <button
        type="button"
        disabled={disabled}
        onClick={onContinue}
        className={`w-full sm:w-80 h-14 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition shadow-[0_10px_25px_rgba(19,35,95,0.25)]
          ${
            disabled
              ? 'bg-slate-300 text-slate-500 cursor-not-allowed shadow-none'
              : 'bg-[#13235f] text-white hover:bg-[#0d1a4f]'
          }`}
      >
        Continue <FaArrowRight size={16} />
      </button>
    )}
  </div>
);

export const SummaryItem = ({ label, value }) => (
  <div>
    <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
    <p className="m-0 font-medium text-slate-800">{value}</p>
  </div>
);
